import * as React from "react";
import { useContext } from "react";
import {
  Rocket,
  LayoutDashboard,
  SquareTerminal,
  Cloud,
  CreditCard,
  FlaskConical,
  ChartSpline,
  Users,
  Book,
  MessageSquareMore,
  MessageCircleQuestion,
  ShieldUser,
  FileTerminal,
} from "lucide-react";
import { useLocation, Link } from "react-router-dom";

import { NavUser } from "./nav-user";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "../../components/ui/sidebar"; // TODO: fix aliasing so we can use @/components/ui/sidebar

import { Urls } from "../../domain/urls";
import { UserContext } from "../../crud/user/context";

export const AppSidebar = (): JSX.Element => {
  const location = useLocation();
  const { user } = useContext(UserContext);
  const isStaff = user.isStaff;

  const sidebarData = {
    platform: [
      {
        title: "Get Started",
        url: Urls.GetStarted,
        icon: Rocket,
      },
      {
        title: "Dashboard",
        url: Urls.Clusters,
        icon: LayoutDashboard,
      },
      ...(user.shouldSeeNotebookPage
        ? [
            {
              title: "Notebooks",
              url: Urls.Notebooks,
              icon: FlaskConical,
            },
          ]
        : []),
      {
        title: "Software",
        url: Urls.Software,
        icon: SquareTerminal,
      },
      {
        title: "Cloud",
        url: Urls.Setup,
        icon: Cloud,
      },
      {
        title: "Billing",
        url: Urls.Billing,
        icon: CreditCard,
      },
      {
        title: "Usage",
        url: Urls.Usage,
        icon: ChartSpline,
      },
      {
        title: "Team",
        url: Urls.Team,
        icon: Users,
      },
      ...(isStaff
        ? [
            {
              title: "Batch Jobs",
              url: Urls.Batch,
              icon: FileTerminal,
            },
            {
              title: "Staff",
              url: Urls.CoiledStaff,
              icon: ShieldUser,
            },
          ]
        : []),
    ],
    support: [
      {
        title: "Docs",
        url: Urls.Docs,
        icon: Book,
      },
      {
        title: "Join our Slack",
        url: Urls.Slack,
        icon: MessageSquareMore,
      },
      {
        title: "Contact an Engineer",
        url: "mailto:support@coiled.io",
        icon: MessageCircleQuestion,
      },
    ],
  };

  const isActive = (url: string) => {
    // For absolute URLs (like docs.coiled.io), don't show as active
    if (url.startsWith("http")) return false;
    const currentPath = location.pathname;
    const itemPath = `/${url}`;
    // Check if current path starts with item path to handle subpages
    return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <Link to="/" className="flex w-8 h-10 p-2 mt-2 [&>svg]:size-8">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 9 24"
                preserveAspectRatio="xMidYMid meet"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.34561 10.2732C2.06136 10.2952 0.192054 8.46158 0.170117 6.17733C0.148179 3.89293 1.98178 2.02377 4.26603 2.00184H4.34561V10.2732Z"
                  fill="white"
                  style={{ fill: "white", fillOpacity: 1 }}
                />
                <path
                  d="M7.75268 11.4224C7.75268 9.71796 6.70741 8.18776 5.11914 7.56877V15.2754C6.70683 14.6564 7.75268 13.1266 7.75268 11.4224Z"
                  fill="white"
                  style={{ fill: "white", fillOpacity: 1 }}
                />
                <path
                  d="M5.125 5.4082C6.61863 5.4082 7.82923 4.19804 7.8298 2.70441C7.8298 1.21077 6.61964 3.57628e-05 5.126 -0.000394344H5.125V5.40777V5.4082Z"
                  fill="white"
                  style={{ fill: "white", fillOpacity: 1 }}
                />
                <path
                  d="M2.42969 14.8635C2.42868 16.1849 3.16726 17.3961 4.34286 18V11.7275C3.16826 12.3313 2.42969 13.5416 2.42969 14.8625V14.8635Z"
                  fill="white"
                  style={{ fill: "white", fillOpacity: 1 }}
                />
              </svg>
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarData.platform.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    data-active={isActive(item.url)}
                    className="[&>svg]:opacity-70 hover:[&>svg]:opacity-90 [&[data-active=true]>svg]:opacity-100 [&[data-active=true]>svg]:text-accent"
                  >
                    {item.url.startsWith("http") ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarGroup>
          <SidebarGroupLabel>Support</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarData.support.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    data-active={isActive(item.url)}
                    className="[&>svg]:opacity-70 hover:[&>svg]:opacity-90 [&[data-active=true]>svg]:opacity-100 [&[data-active=true]>svg]:text-accent"
                  >
                    {item.url.startsWith("http") ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
          <NavUser className="mt-2" />
        </SidebarGroup>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
